import { createContext, useContext, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { hashKey, useIsMutating } from "@tanstack/react-query"
import { getMutationKey } from "@trpc/react-query"

import { api, type RouterOutputs } from "~/utils/api"
import { NEW_RULE_ID } from "~/utils/allocationRuleDrafts"
import { RULE_DEFAULTS, diffRuleParams, type RuleFieldChange } from "~/utils/replenishmentRules"
import { formatName } from "~/utils/namingUtils"
import { REFERENCE_ROS_LEVEL_LABELS, visibleRateOfSaleWeights } from "~/utils/rateOfSale"
import { useAllocationStores, type RateOfSaleSettings } from "../useAllocationData"
import { usePackageStaging, type PackageStaging } from "./drafts/usePackageStaging"
import type { StoreDraft } from "./drafts/storeDraftState"
import type { DraftChange } from "../DraftCommandbar"
import { useRuleEditorRules } from "./ruleEditorAdapter"
import { invalidatePackageReaders, invalidateStoreReaders } from "./usePackageData"
import {
  EMPTY_PRODUCT_SETTINGS_DRAFT,
  type ProductSettingsDraft,
} from "./products/productSettingsModel"

import { effectiveReviewCycleDays, type RuleParams } from "~/utils/replenishmentRules"

/** Staged rule edits (field patches per rule id; NEW_RULE_ID for an unsaved rule). */
export type RuleDraft = Partial<RuleParams> & { name?: string; description?: string | null }

/** Days-mode target-card values, stored apart from the settings draft so card
 * edits never move the target-settings numbers before saving. All three are
 * cover windows in days; the overstock factor derives only on save. */
export type CardDraft = { coverMinDays: number; coverOptDays: number; coverOverDays: number }

/** The rate of sale settings Settings › Rate of sale edits. */
type RosValues = Pick<
  RateOfSaleSettings,
  | "windowDays"
  | "weightRecentPct"
  | "weightMidPct"
  | "weightOldPct"
  | "minDaysWithStock"
  | "referenceRosLevels"
  | "referenceRosMinLines"
  | "referenceRosPositiveOnly"
  | "noRecentSalesDays"
  | "referenceRosWarnLevel"
>

export type RateOfSaleDraft = Partial<RosValues>

type RosChangeGroup = {
  key: string
  fields: (keyof RosValues)[]
  label: () => string
  show: (values: RosValues) => string
}

/** Only flags grid lines: changing it alone leaves the rate of sale as it is. */
export const NO_RECENT_SALES_CHANGE = "noRecentSales"
const WARN_LEVEL_CHANGE = "referenceWarnLevel"

export const isFlagOnlyChange = (key: string) =>
  key === NO_RECENT_SALES_CHANGE || key === WARN_LEVEL_CHANGE

/** One group per unsaved change: the period weights move together, so they count as one. */
const ROS_CHANGE_GROUPS: RosChangeGroup[] = [
  {
    key: "window",
    fields: ["windowDays"],
    label: () => `${formatName("sale", true, true)} history window`,
    show: (s) => `${s.windowDays} days`,
  },
  {
    key: "weights",
    fields: ["weightRecentPct", "weightMidPct", "weightOldPct"],
    label: () => `${formatName("sale", true, true)} period weights`,
    show: (s) => `${visibleRateOfSaleWeights(s, s.windowDays).join(" / ")} %`,
  },
  {
    key: "minDays",
    fields: ["minDaysWithStock"],
    label: () => "Minimum days with inventory",
    show: (s) => `${s.minDaysWithStock} days`,
  },
  {
    key: "referencePositiveOnly",
    fields: ["referenceRosPositiveOnly"],
    label: () => "Reference ROS only averages lines with a rate above 0",
    show: (s) => (s.referenceRosPositiveOnly ? "On" : "Off"),
  },
  {
    key: "referenceMinLines",
    fields: ["referenceRosMinLines"],
    label: () => "Reference ROS minimum lines",
    show: (s) => `${s.referenceRosMinLines} lines`,
  },
  {
    key: NO_RECENT_SALES_CHANGE,
    fields: ["noRecentSalesDays"],
    label: () => `No recent ${formatName("sale", true)} flag`,
    show: (s) => (s.noRecentSalesDays === 0 ? "Off" : `${s.noRecentSalesDays} days`),
  },
  {
    key: WARN_LEVEL_CHANGE,
    fields: ["referenceRosWarnLevel"],
    label: () => "Reference ROS warning level",
    show: (s) =>
      s.referenceRosWarnLevel == null
        ? "Off"
        : `Level ${s.referenceRosLevels.indexOf(s.referenceRosWarnLevel) + 1}`,
  },
  {
    key: "referenceLevels",
    fields: ["referenceRosLevels"],
    label: () => "Reference ROS levels",
    show: (s) =>
      s.referenceRosLevels.length === 0
        ? "None"
        : s.referenceRosLevels.map((level) => REFERENCE_ROS_LEVEL_LABELS[level].title).join(", "),
  },
]

function sameValue(a: RosValues[keyof RosValues], b: RosValues[keyof RosValues]) {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((value, index) => value === b[index])
  }
  return a === b
}

/** The save (and preview) input for these rate of sale settings. */
export function toRateOfSaleInput(settings: RateOfSaleSettings) {
  return {
    calculationLevel: settings.calculationLevel,
    windowDays: settings.windowDays,
    weightRecentPct: settings.weightRecentPct,
    weightMidPct: settings.weightMidPct,
    weightOldPct: settings.weightOldPct,
    minDaysWithStock: settings.minDaysWithStock,
    referenceRosLevels: settings.referenceRosLevels,
    referenceRosMinLines: settings.referenceRosMinLines,
    referenceRosPositiveOnly: settings.referenceRosPositiveOnly,
    seasonalityIndex: settings.seasonalityIndex,
    noRecentSalesDays: settings.noRecentSalesDays,
    referenceRosWarnLevel: settings.referenceRosWarnLevel,
  }
}

/** Staged rate of sale changes, one per group, for the Details panel and the count. */
export function rateOfSaleChanges(
  saved: RateOfSaleSettings,
  draft: RateOfSaleDraft,
): DraftChange[] {
  const next = { ...saved, ...draft }
  return ROS_CHANGE_GROUPS.filter((group) =>
    group.fields.some((field) => !sameValue(next[field], saved[field])),
  ).map((group) => ({
    key: group.key,
    label: group.label(),
    before: group.show(saved),
    after: group.show(next),
  }))
}

/** The params saving would produce for a rule: card values convert to the
 * coupled settings fields only here. The review period (and with it a delivery
 * calendar) is only touched when the ROP/OUL spread actually changed it. */
export function paramsWithCards(effective: RuleParams, cardDraft: CardDraft | null): RuleParams {
  if (!cardDraft || effective.thresholdMode !== "days") return effective
  const cardReview = Math.max(0.1, cardDraft.coverOptDays - cardDraft.coverMinDays)
  // With departure days picked, the calendar sets the review period, so the cards keep it.
  const cardReviewChanged =
    !(effective.cycleMode === "schedule" && effective.deliveryDays.length > 0) &&
    Math.abs(cardReview - effectiveReviewCycleDays(effective)) >= 0.05
  return {
    ...effective,
    safetyCoverDays: cardDraft.coverMinDays,
    ...(cardReviewChanged ? { cycleMode: "fixed" as const, reviewCycleDays: cardReview } : {}),
    overstockFactor:
      cardDraft.coverOptDays > 0
        ? Math.max(1, Math.round((cardDraft.coverOverDays / cardDraft.coverOptDays) * 100) / 100)
        : effective.overstockFactor,
  }
}

/** Setup-wide staging: rule, target-card, store→rule, package store-list, rate of
 * sale, product settings and store metadata drafts. Only the open page holds any: its
 * Commandbar asks to save or discard before another page opens. */
type SetupDraftsValue = {
  drafts: Record<string, RuleDraft>
  setDrafts: React.Dispatch<React.SetStateAction<Record<string, RuleDraft>>>
  cardDrafts: Record<string, CardDraft | null>
  setCardDrafts: React.Dispatch<React.SetStateAction<Record<string, CardDraft | null>>>
  assignDraft: Record<string, string | null>
  setAssignDraft: React.Dispatch<React.SetStateAction<Record<string, string | null>>>
  packageStaging: PackageStaging
  rateOfSaleDraft: RateOfSaleDraft
  setRateOfSaleDraft: React.Dispatch<React.SetStateAction<RateOfSaleDraft>>
  productSettingsDraft: ProductSettingsDraft
  setProductSettingsDraft: React.Dispatch<React.SetStateAction<ProductSettingsDraft>>
  storeDraft: StoreDraft
  setStoreDraft: React.Dispatch<React.SetStateAction<StoreDraft>>
  /** One save mutation per draft kind, so every consumer sees the same saving state. */
  publishPackageDraft: ReturnType<typeof api.allocation.publishPackageDraft.useMutation>
  saveStores: ReturnType<typeof api.allocation.saveStores.useMutation>
}

const SetupDraftsContext = createContext<SetupDraftsValue | null>(null)

export function SetupDraftsProvider({ children }: { children: React.ReactNode }) {
  const [drafts, setDrafts] = useState<Record<string, RuleDraft>>({})
  const [cardDrafts, setCardDrafts] = useState<Record<string, CardDraft | null>>({})
  const [assignDraft, setAssignDraft] = useState<Record<string, string | null>>({})
  const packageStaging = usePackageStaging()
  const [rateOfSaleDraft, setRateOfSaleDraft] = useState<RateOfSaleDraft>({})
  const [productSettingsDraft, setProductSettingsDraft] = useState<ProductSettingsDraft>(
    EMPTY_PRODUCT_SETTINGS_DRAFT,
  )
  const [storeDraft, setStoreDraft] = useState<StoreDraft>({})
  const utils = api.useUtils()
  // Awaited before the caller's onSuccess clears the draft, so the page never shows the old tree.
  const publishPackageDraft = api.allocation.publishPackageDraft.useMutation({
    onSuccess: () => invalidatePackageReaders(utils),
  })
  const saveStores = api.allocation.saveStores.useMutation({
    onSuccess: () => invalidateStoreReaders(utils),
  })
  const value = useMemo(
    () => ({
      drafts,
      setDrafts,
      cardDrafts,
      setCardDrafts,
      assignDraft,
      setAssignDraft,
      packageStaging,
      rateOfSaleDraft,
      setRateOfSaleDraft,
      productSettingsDraft,
      setProductSettingsDraft,
      storeDraft,
      setStoreDraft,
      publishPackageDraft,
      saveStores,
    }),
    [
      drafts,
      cardDrafts,
      assignDraft,
      packageStaging,
      rateOfSaleDraft,
      productSettingsDraft,
      storeDraft,
      publishPackageDraft,
      saveStores,
    ],
  )
  return <SetupDraftsContext.Provider value={value}>{children}</SetupDraftsContext.Provider>
}

/** Hands the drafts to an inspector rendered outside the provider: the Module draws it beside the page. */
export function useSetupDraftsBridge(Inspector: React.ComponentType | undefined) {
  const value = useSetupDrafts()
  return useMemo(
    () =>
      Inspector === undefined ? undefined : (
        <SetupDraftsContext.Provider value={value}>
          <Inspector />
        </SetupDraftsContext.Provider>
      ),
    [Inspector, value],
  )
}

export function useSetupDrafts(): SetupDraftsValue {
  const value = useContext(SetupDraftsContext)
  if (value === null) throw new Error("useSetupDrafts must be used inside SetupDraftsProvider")
  return value
}

/** Field changes per staged rule id, resolved against the saved rules. */
export function useStagedChanges() {
  const { drafts, cardDrafts } = useSetupDrafts()
  const { rules } = useRuleEditorRules()

  return useMemo(() => {
    const ruleById = new Map(rules.map((rule) => [rule.id, rule]))
    const savedParamsOf = (ruleId: string): RuleParams | null => {
      const saved = ruleById.get(ruleId)
      if (!saved) return null
      return {
        ...saved,
        thresholdMode: saved.thresholdMode === "units" ? "units" : "days",
        cycleMode: saved.cycleMode === "schedule" ? "schedule" : "fixed",
      }
    }

    // A rule is staged when it has field edits (drafts) OR target-card edits
    // (cardDrafts) — card-only changes must surface too.
    const stagedRuleIds = [
      ...new Set([
        ...Object.keys(drafts),
        ...Object.entries(cardDrafts)
          .filter(([, cardDraft]) => cardDraft !== null)
          .map(([ruleId]) => ruleId),
      ]),
    ]
    const stagedChanges = new Map<string, RuleFieldChange[]>()
    for (const ruleId of stagedRuleIds) {
      const draft = drafts[ruleId] ?? {}
      const isNew = ruleId === NEW_RULE_ID
      const saved = isNew ? null : savedParamsOf(ruleId)
      if (!isNew && saved === null) continue
      const base = saved ?? RULE_DEFAULTS
      const effective = paramsWithCards({ ...base, ...draft }, cardDrafts[ruleId] ?? null)
      const changes = diffRuleParams(base, effective)
      const savedName = ruleById.get(ruleId)?.name
      const savedDescription = ruleById.get(ruleId)?.description ?? null
      if (
        !isNew &&
        draft.description !== undefined &&
        (draft.description ?? null) !== savedDescription
      ) {
        changes.unshift({
          label: "Description",
          field: "description",
          before: savedDescription ?? "—",
          after: draft.description ?? "—",
        })
      }
      if (!isNew && draft.name !== undefined && draft.name !== savedName) {
        changes.unshift({
          label: "Name",
          field: "name",
          before: savedName ?? "—",
          after: draft.name,
        })
      }
      if (isNew || changes.length > 0) stagedChanges.set(ruleId, changes)
    }

    return stagedChanges
  }, [drafts, cardDrafts, rules])
}

/** Staged store→rule moves that differ from the store's saved rule. */
export function usePendingAssignments() {
  const { assignDraft } = useSetupDrafts()
  const { stores } = useAllocationStores()
  return useMemo(() => {
    const storeById = new Map(stores.map((store) => [store.id, store]))
    return Object.entries(assignDraft).flatMap(([storeId, toRuleId]) => {
      const store = storeById.get(storeId)
      if (store === undefined || store.replenishmentRuleId === toRuleId) return []
      return [
        { storeId, storeName: store.storeNumber, fromRuleId: store.replenishmentRuleId, toRuleId },
      ]
    })
  }, [assignDraft, stores])
}

type PendingAssignment = ReturnType<typeof usePendingAssignments>[number]

/** The staged store moves a rule's save carries: moves onto the unsaved new rule wait for that rule. */
export function assignmentsSavedWith(ruleId: string | null, pending: PendingAssignment[]) {
  return ruleId === NEW_RULE_ID ? pending : pending.filter((move) => move.toRuleId !== NEW_RULE_ID)
}

/** Details rows for one staged rule: a row for a new rule, then one per changed field. */
export function ruleDraftChanges(
  ruleId: string,
  changes: RuleFieldChange[],
  drafts: Record<string, RuleDraft>,
): DraftChange[] {
  const fields = changes.map((change) => ({
    key: change.field,
    label: change.label,
    code: change.field,
    before: change.before,
    after: change.after,
  }))
  if (ruleId !== NEW_RULE_ID) return fields
  const created = {
    key: "created",
    label: "Rule created",
    before: "—",
    after: drafts[ruleId]?.name ?? "New rule",
  }
  return [created, ...fields]
}

/** Details rows for staged store moves: saved rule name before, staged name after. */
export function ruleAssignmentChanges(
  pending: PendingAssignment[],
  rules: { id: string; name: string }[],
  drafts: Record<string, RuleDraft>,
): DraftChange[] {
  const savedName = new Map(rules.map((rule) => [rule.id, rule.name]))
  const before = (ruleId: string | null) =>
    ruleId === null ? "No rule" : (savedName.get(ruleId) ?? ruleId)
  const after = (ruleId: string | null) => {
    if (ruleId === null) return "No rule"
    if (ruleId === NEW_RULE_ID) return drafts[ruleId]?.name ?? "New rule"
    return drafts[ruleId]?.name ?? before(ruleId)
  }
  return pending.map(({ storeId, storeName, fromRuleId, toRuleId }) => ({
    key: `store:${storeId}`,
    label: storeName,
    before: before(fromRuleId),
    after: after(toRuleId),
  }))
}

/** A save's suggested qty run waits while another calculation is going, so say when it runs. */
export function noteRunAfterCurrent(
  run: Pick<RouterOutputs["allocation"]["saveRule"]["run"], "status">,
) {
  if (run.status === "after_current") {
    toast.info("Published. Suggested qty is recalculated when the current calculation finishes.")
  }
}

const SETTINGS_SAVE_KEYS = new Set(
  [
    api.allocation.saveRule,
    api.allocation.saveRateOfSaleSettings,
    api.allocation.publishPackageDraft,
    api.allocation.saveStores,
    api.allocation.saveProductSettings,
  ].map((procedure) => hashKey(getMutationKey(procedure))),
)

/** True while a sub-tab save runs. Its draft cleanup lives in the sub-tab's `mutate` callbacks,
 * which TanStack drops when the sub-tab unmounts. */
export function useSettingsSaving() {
  return (
    useIsMutating({
      predicate: (mutation) => SETTINGS_SAVE_KEYS.has(hashKey(mutation.options.mutationKey ?? [])),
    }) > 0
  )
}
